import api from '../../../services/api';

const BASE_PATH = '/ad-rules';

class AdRuleAPIService {
  constructor() {
    this.api = api;
    this.basePath = BASE_PATH;
  }

  /**
   * Lấy danh sách rules
   */
  async getRules(params = {}) {
    try {
      const response = await this.api.get(this.basePath, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Lấy rule theo ID
   */
  async getRule(ruleId) {
    try {
      const response = await this.api.get(`${this.basePath}/${ruleId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Tạo rule mới
   */
  async createRule(ruleData) {
    try {
      const response = await this.api.post(this.basePath, ruleData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Cập nhật rule
   */
  async updateRule(ruleId, updateData) {
    try {
      const response = await this.api.put(`${this.basePath}/${ruleId}`, updateData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Xóa rule
   */
  async deleteRule(ruleId) {
    try {
      const response = await this.api.delete(`${this.basePath}/${ruleId}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Toggle trạng thái active/inactive
   */
  async toggleRuleStatus(ruleId) {
    try {
      const response = await this.api.patch(`${this.basePath}/${ruleId}/toggle-status`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Duplicate rule
   */
  async duplicateRule(ruleId) {
    try {
      const response = await this.api.post(`${this.basePath}/${ruleId}/duplicate`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Lấy active rules
   */
  async getActiveRules(platform = null) {
    try {
      const params = platform ? { platform } : {};
      const response = await this.api.get(`${this.basePath}/active`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Save complete rule (create or update based on existence)
   */
  async saveRule(ruleData) {
    try {
      if (ruleData.id) {
        // Update existing rule
        return await this.updateRule(ruleData.id, ruleData);
      } else {
        // Create new rule
        return await this.createRule(ruleData);
      }
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update execution stats
   */
  async updateExecutionStats(ruleId, stats) {
    try {
      const response = await this.api.post(`${this.basePath}/${ruleId}/execution-stats`, stats);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get rule types (constants for frontend)
   */
  getRuleTypes() {
    return [
      { value: 'budget_optimization', label: 'Tối ưu ngân sách' },
      { value: 'bid_optimization', label: 'Tối ưu bid' },
      { value: 'audience_optimization', label: 'Tối ưu đối tượng' },
      { value: 'creative_optimization', label: 'Tối ưu creative' },
      { value: 'keyword_optimization', label: 'Tối ưu từ khóa' }
    ];
  }

  /**
   * Get platform options
   */
  getPlatforms() {
    return [
      { value: 'google_ads', label: 'Google Ads' },
      { value: 'facebook_ads', label: 'Facebook Ads' },
      { value: 'tiktok_ads', label: 'TikTok Ads' },
      { value: 'amazon_ads', label: 'Amazon Ads' }
    ];
  }

  /**
   * Get frequency options
   */
  getFrequencies() {
    return [
      { value: 'manual', label: 'Thủ công' },
      { value: 'hourly', label: 'Mỗi giờ' },
      { value: 'daily', label: 'Hàng ngày' },
      { value: 'weekly', label: 'Hàng tuần' }
    ];
  }

  /**
   * Validate rule data before saving
   */
  validateRuleData(ruleData) {
    const errors = [];

    if (!ruleData.name?.trim()) {
      errors.push('Tên rule không được để trống');
    }

    if (!ruleData.type) {
      errors.push('Loại rule không được để trống');
    }

    if (!ruleData.conditions || Object.keys(ruleData.conditions).length === 0) {
      errors.push('Điều kiện rule không được để trống');
    }

    if (!ruleData.actions || Object.keys(ruleData.actions).length === 0) {
      errors.push('Hành động rule không được để trống');
    }

    if (!ruleData.platform) {
      errors.push('Platform không được để trống');
    }

    if (ruleData.priority && (ruleData.priority < 1 || ruleData.priority > 10)) {
      errors.push('Độ ưu tiên phải từ 1-10');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Handle API errors
   */
  handleError(error) {
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
    const errorDetails = error.response?.data?.errors || [];
    
    return {
      message: errorMessage,
      details: errorDetails,
      status: error.response?.status || 500,
      success: false
    };
  }
}

// Export singleton instance
export default new AdRuleAPIService();